import { ARIA, EVENTS, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';
import ImageCaption from '@bs/components/views/ImageCaption';

// Pixels scrolled per second during auto-play. Tune here.
const PX_PER_SECOND = 40;

// Minimum pixels the pointer must move before we treat the interaction as a
// drag. Below this threshold the interaction is considered a click and pointer
// capture is never acquired, so child elements (e.g. caption buttons) receive
// their events normally.
const DRAG_THRESHOLD = 5;

/**
 * A class for the Hero Slider module.
 */
export default class HeroSlider extends MessageBus {
  /**
   * @param {HTMLElement} element - the module's container
   */
  constructor(element) {
    super();

    /** @type {HTMLElement} */
    this.element = element;

    // ── scroll state ────────────────────────────────────────────────────────
    /** Current horizontal scroll position in pixels (always 0–originalWidth) */
    this.scrollX = 0;

    /** Pixel width of one complete set of real slides (including inter-slide gaps) */
    this.originalWidth = 0;

    /** Number of original (non-cloned) slides */
    this.originalCount = 0;

    // ── interaction flags ───────────────────────────────────────────────────
    /** True while the cursor is inside the track — pauses auto-scroll */
    this.isPaused = ARIA.FALSE;

    /**
     * Drag phases:
     *   'idle'     — no pointer contact
     *   'pending'  — pointerdown received, waiting to see if it becomes a drag
     *   'dragging' — pointer has moved past DRAG_THRESHOLD; pointer is captured
     */
    this.dragPhase = 'idle';

    /** clientX at the moment pointerdown fired */
    this.dragStartX = 0;

    /** scrollX at the moment pointerdown fired */
    this.dragStartScrollX = 0;

    /**
     * Pointer ID being tracked — used for setPointerCapture once the drag
     * threshold is crossed.
     */
    this.activePointerId = null;

    /**
     * True when the pointer was released after a confirmed drag.
     */
    this.didDrag = ARIA.FALSE;

    /** requestAnimationFrame handle — kept so destroy() can cancel it */
    this.rafId = null;

    /**
     * ImageCaption instances created for the cloned slides. Kept so destroy()
     * can tear them (and their document-level listeners) down.
     * @type {ImageCaption[]}
     */
    this.cloneComponents = [];

    this.init();
  }

  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  cacheDomReferences() {
    this.trackEl = this.element.querySelector(SELECTORS.HERO_SLIDER_SWIPER);
    this.wrapperEl = this.trackEl?.querySelector(SELECTORS.HERO_SLIDER_WRAPPER);
    return this;
  }

  setupHandlers() {
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handleClick = this.handleClick.bind(this);
    return this;
  }

  enable() {
    if (!this.trackEl || !this.wrapperEl) return this;

    this.duplicateSlides();
    this.computeOriginalWidth();
    this.bindEvents();
    this.startScroll();

    return this;
  }

  destroy() {
    cancelAnimationFrame(this.rafId);
    this.trackEl.removeEventListener(EVENTS.MOUSEENTER, this.handleMouseEnter);
    this.trackEl.removeEventListener(EVENTS.MOUSELEAVE, this.handleMouseLeave);
    this.trackEl.removeEventListener(
      EVENTS.POINTERDOWN,
      this.handlePointerDown
    );
    this.trackEl.removeEventListener(
      EVENTS.POINTERMOVE,
      this.handlePointerMove
    );
    this.trackEl.removeEventListener(EVENTS.POINTERUP, this.handlePointerUp);
    this.trackEl.removeEventListener(
      EVENTS.POINTERCANCEL,
      this.handlePointerUp
    );
    this.trackEl.removeEventListener(EVENTS.CLICK, this.handleClick);

    // Tear down the ImageCaption instances we created for the clones so their
    // document-level listeners don't leak.
    this.cloneComponents.forEach((instance) => {
      if (instance && typeof instance.destroy === 'function') {
        instance.destroy();
      }
    });
    this.cloneComponents = [];

    return this;
  }

  /**
   * Appends a cloned copy of every original slide to the wrapper.
   *
   * cloneNode(true) copies markup but NOT event listeners, so a cloned caption
   * button would be inert. Rather than forward its clicks to the (off-screen)
   * original — which opened the wrong element — we give every clone its own
   * real ImageCaption instance. The clone you see is the clone that responds.
   */
  duplicateSlides() {
    const originals = Array.from(this.wrapperEl.children);
    this.originalCount = originals.length;

    originals.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[tabindex], a, button, input').forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });

      // Prevent any global component loader from also initialising these
      // clones (which would double-bind and cancel each toggle). We own them.
      clone
        .querySelectorAll('[data-loadcomponent]')
        .forEach((el) => el.removeAttribute('data-loadcomponent'));

      this.wrapperEl.appendChild(clone);

      // Hydrate each caption inside the clone with its own live instance.
      clone.querySelectorAll('[data-image-caption]').forEach((captionEl) => {
        try {
          this.cloneComponents.push(new ImageCaption(captionEl));
        } catch (error) {
          // Fail soft: a broken import path shouldn't kill the slider.
          // eslint-disable-next-line no-console
          console.error('HeroSlider: failed to init clone caption', error);
        }
      });
    });
  }

  /**
   * Measures the pixel distance that equals one full scroll cycle.
   */
  computeOriginalWidth() {
    const gap =
      parseFloat(window.getComputedStyle(this.wrapperEl).columnGap) || 0;

    const originals = Array.from(this.wrapperEl.children).slice(
      0,
      this.originalCount
    );

    this.originalWidth =
      originals.reduce((total, slide) => total + slide.offsetWidth, 0) +
      this.originalCount * gap;
  }

  /**
   * Normalises any scroll value into [0, originalWidth) using modular arithmetic.
   * @param {number} value - input scroll value in pixels
   * @returns {number} Normalised scroll value in range [0, originalWidth)
   */
  wrapScroll(value) {
    return (
      ((value % this.originalWidth) + this.originalWidth) % this.originalWidth
    );
  }

  /** Writes the current scrollX to the DOM — single point of truth. */
  applyTransform() {
    this.wrapperEl.style.transform = `translateX(${-this.scrollX}px)`;
  }

  handleMouseEnter() {
    this.isPaused = true;
  }

  handleMouseLeave() {
    this.isPaused = false;
  }

  /**
   * Begin pointer interaction; don't capture yet until drag threshold.
   * @param {PointerEvent} e - pointerdown event
   * @returns {void}
   */
  handlePointerDown(e) {
    this.dragPhase = 'pending';
    this.didDrag = false;
    this.dragStartX = e.clientX;
    this.dragStartScrollX = this.scrollX;
    this.activePointerId = e.pointerId;
  }

  /**
   * Track pointer movement; commit to drag after threshold.
   * @param {PointerEvent} e - pointermove event
   * @returns {void}
   */
  handlePointerMove(e) {
    if (this.dragPhase === EVENTS.IDLE) return;

    const distance = Math.abs(e.clientX - this.dragStartX);

    if (this.dragPhase === EVENTS.PENDING) {
      // Has the pointer moved far enough to commit to a drag?
      if (distance < DRAG_THRESHOLD) return;

      // Threshold crossed — lock in as a drag and capture the pointer.
      this.dragPhase = EVENTS.DRAGGING;
      this.trackEl.setPointerCapture(this.activePointerId);
    }

    const delta = this.dragStartX - e.clientX;
    this.scrollX = this.wrapScroll(this.dragStartScrollX + delta);
    this.applyTransform();
  }

  handlePointerUp() {
    if (this.dragPhase === EVENTS.DRAGGING) {
      // Mark that a real drag just finished so handleClick can suppress the
      // synthetic click the browser fires immediately after pointerup.
      this.didDrag = true;
    }

    this.dragPhase = EVENTS.IDLE;
    this.activePointerId = null;
  }

  /**
   * The browser fires a click event after every pointerup, even when the
   * pointer has been dragged. Suppress it for exactly one event after a drag
   * so that releasing a drag on top of a button doesn't activate it.
   */
  /**
   * Suppress the synthetic click event fired after a drag.
   * @param {MouseEvent} e - click event
   * @returns {void}
   */
  handleClick(e) {
    if (this.didDrag) {
      e.stopPropagation();
      this.didDrag = false;
    }
  }

  bindEvents() {
    this.trackEl.addEventListener(EVENTS.MOUSEENTER, this.handleMouseEnter);
    this.trackEl.addEventListener(EVENTS.MOUSELEAVE, this.handleMouseLeave);
    this.trackEl.addEventListener(EVENTS.POINTERDOWN, this.handlePointerDown);
    this.trackEl.addEventListener(EVENTS.POINTERMOVE, this.handlePointerMove);
    this.trackEl.addEventListener(EVENTS.POINTERUP, this.handlePointerUp);
    // pointercancel fires when the browser takes over (e.g. iOS scroll takeover)
    this.trackEl.addEventListener(EVENTS.POINTERCANCEL, this.handlePointerUp);
    // Capture phase so we intercept before the button's own listener
    this.trackEl.addEventListener(EVENTS.CLICK, this.handleClick, true);

    return this;
  }

  startScroll() {
    let lastTimestamp = null;

    const tick = (timestamp) => {
      this.rafId = requestAnimationFrame(tick);

      // Paused by hover or mid-drag — reset elapsed timer so there is no
      // position jump when scrolling resumes.
      if (this.isPaused || this.dragPhase !== EVENTS.IDLE) {
        lastTimestamp = null;
        return;
      }

      if (lastTimestamp !== null) {
        const elapsed = timestamp - lastTimestamp;
        const delta = (PX_PER_SECOND * elapsed) / 1000;
        this.scrollX = this.wrapScroll(this.scrollX + delta);
        this.applyTransform();
      }

      lastTimestamp = timestamp;
    };

    this.rafId = requestAnimationFrame(tick);
    return this;
  }
}
